import { useCallback, useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import compose from 'lodash/flowRight';
import get from 'lodash/get';
import { inject, observer } from 'mobx-react';
import { toast } from 'react-toastify';

import SelectPickupWindow from '../SelectPickupWindow';
import { DEFAULT_DSP_ETA_COUNTDOWN_SECONDS } from '../../constants/eta';
import styles from './styles';


function EditPickupWindowModal(props) {
  const { open, assignmentInfo, onClose, onCompleted, store } = props;
  const { assignmentStore } = store;

  const assignment = assignmentInfo?.assignment;
  const driver = assignmentInfo?.driver;

  const currentPickupWindow = useMemo(
    () =>
      assignment?.start_pickup_time && assignment?.end_pickup_time && assignment?.pickup_window_id
        ? { start_ts: assignment.start_pickup_time, end_ts: assignment.end_pickup_time, id: assignment.pickup_window_id }
        : null,
    [assignment?.start_pickup_time, assignment?.end_pickup_time, assignment?.pickup_window_id],
  );

  const [pickupWindowId, setPickupWindowId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!open) {
      setPickupWindowId(null);
      return undefined;
    }
    setIsSaving(false);
    setCountdownSeconds(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);
    let active = true;
    assignmentStore
      .loadDspEtaCountdownSeconds(assignment?.region_code, assignment?.warehouse_id)
      .then((seconds) => {
        if (active) setCountdownSeconds(seconds);
      })
      .catch(() => {
        if (active) setCountdownSeconds(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const handleResult = (r) => {
    if (r?.ok) {
      toast.success('Pickup window updated successfully.');
      onClose?.();
      onCompleted?.();
    } else {
      toast.error(get(r, 'data.message', 'Failed to rebook pickup window. Please try again.'));
    }
  };

  const onSave = async () => {
    if (!assignment?.id || !pickupWindowId) return;
    setIsSaving(true);
    try {
      const r = await assignmentStore.rebookEta(assignment.id, pickupWindowId);
      handleResult(r);
    } catch (e) {
      toast.error(get(e, 'data.message', 'Failed to rebook pickup window. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const onPickupWindowTimeout = useCallback(() => {
    if (isSaving) return;
    onClose?.();
    toast.error(
      `Rebooking pickup window for assignment ${assignment?.label} is incomplete as pickup window was not selected in time.`,
    );
  }, [isSaving, onClose, assignment]);

  if (!assignment) return null;

  const savingSpinner = isSaving ? <CircularProgress size={15} thickness={1} color="inherit" /> : null;

  return (
    <Dialog maxWidth="lg" open={open} onClose={onClose} PaperProps={{ sx: styles.dialogPaper }}>
      <DialogTitle sx={styles.dialogTitle}>Rebook pickup window</DialogTitle>
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.dialogContent}>
        <SelectPickupWindow
          assignment={assignment}
          driver={driver}
          currentWindow={currentPickupWindow}
          selectedWindowId={pickupWindowId}
          onSelectWindow={setPickupWindowId}
          onTimeout={onPickupWindowTimeout}
          countdownSeconds={countdownSeconds}
        />
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Box sx={styles.spacer} />
        <Button variant="outlined" disableElevation size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          size="small"
          disabled={!pickupWindowId || isSaving}
          onClick={onSave}
          startIcon={savingSpinner}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EditPickupWindowModalCompose = compose(inject('store'), observer)(EditPickupWindowModal);

export default EditPickupWindowModalCompose;
